/* import __COLOCATED_TEMPLATE__ from './mention-typeahead.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type Composer from '@intercom/composer-ember/lib/composer';
import { type TypeaheadMatchResult } from '@intercom/composer-ember';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import type Session from 'embercom/services/session';
import { AdminSearchContexts } from 'embercom/objects/inbox/macro';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import { EntityType } from 'embercom/models/data/entity-types';
import type SlackUsersService from 'embercom/services/slack-users-service';
import type { SlackUser } from 'embercom/services/slack-users-service';

interface Args {
  composer: Composer;
  matchResult: TypeaheadMatchResult | null;
  registerDelegate(f: Function): void;
  deregisterDelegate(f: Function): void;
  attrs?: { conversation: any; mentionType?: string };
}

interface Signature {
  Args: Args;
}

interface SlackUserMentionData {
  id: string;
  name: string;
  avatarUrl: string;
  inChannel: boolean;
  type: 'user' | 'usergroup';
}

type MentionItem =
  | { data: AdminWithPermissions; entityType: EntityType.Admin | EntityType.Team }
  | { data: SlackUserMentionData; entityType: EntityType.SlackUser };

const MAX_MENTION_SEARCH_LENGTH = 50;
const MAX_MENTION_NUM_SPACES = 2;
const MAX_MENTIONS_RESULTS = 8;

export default class MentionTypeahead extends Component<Signature> {
  @service declare session: Session;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare slackUsersService: SlackUsersService;
  @service regionService: any;
  @service intercomEventService: any;

  @tracked slackUsers: SlackUser[] = [];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.isSlackMention) {
      this.loadSlackUsers();
    }
  }

  get isLoadingSlackUsers() {
    return this.slackUsersService.isLoading;
  }

  get itemsWidth(): number | undefined {
    return this.isSlackMention ? 400 : undefined;
  }

  get isSlackMention(): boolean {
    return this.args.attrs?.mentionType === 'slack';
  }

  get slackChannelId(): string | null {
    if (!this.args.attrs?.conversation) {
      return null;
    }

    let conversation = this.args.attrs.conversation;

    // Check linkedChannel first for side conversations
    if (conversation.linkedChannel?.channel_id) {
      return conversation.linkedChannel.channel_id;
    }

    let slackChannelAttribute = conversation.attributes?.find(
      (attr: any) => attr.descriptor?.id === 'slack_channel',
    );
    if (!slackChannelAttribute) {
      slackChannelAttribute = conversation.attributes?.find(
        (attr: any) => attr.descriptor?.id === 'slack_dm_channel',
      );
    }
    if (typeof slackChannelAttribute?.value !== 'number') {
      return null;
    }

    return slackChannelAttribute.value.toString();
  }

  async loadSlackUsers() {
    let channelId = this.slackChannelId;
    if (!channelId) {
      return;
    }

    try {
      this.slackUsers = await this.slackUsersService.fetchSlackChannelMembers(channelId);
    } catch (error) {
      console.error('[MentionTypeahead] Error loading Slack channel members:', error);
      this.slackUsers = [];
    }
  }

  get isPrivateChannel(): boolean {
    return this.slackUsersService.isPrivateChannel(this.slackChannelId);
  }

  get query() {
    return this.args.matchResult?.text || '';
  }

  get items() {
    if (this.query.search('@') === -1 || this.query === '@ ') {
      return [];
    }

    let searchTerm = this.query.replace('@', '');
    let numSpaces = searchTerm.split(' ').length - 1;

    if (searchTerm.length > MAX_MENTION_SEARCH_LENGTH || numSpaces > MAX_MENTION_NUM_SPACES) {
      return [];
    }

    if (this.isSlackMention) {
      return this.getSlackUserItems(searchTerm);
    }

    return this.inbox2AssigneeSearch.filterAdminsAndTeams(
      searchTerm,
      AdminSearchContexts.Mentions,
      MAX_MENTIONS_RESULTS,
    ).results;
  }

  private getSlackUserItems(searchTerm: string) {
    if (this.isLoadingSlackUsers) {
      return [];
    }

    let filteredUsers = this.slackUsersService.filterUsers(this.slackUsers, searchTerm);
    return filteredUsers.slice(0, MAX_MENTIONS_RESULTS).map((user) => ({
      data: {
        id: user.id,
        name: user.name,
        avatarUrl: user.avatar_url,
        inChannel: user.in_channel,
        type: user.type,
      },
      entityType: EntityType.SlackUser,
    }));
  }

  @action
  insertMention(selectedItem: MentionItem) {
    if (!this.args.matchResult) {
      return;
    }

    let prosemirrorState = this.args.composer.state.prosemirrorState;

    if (prosemirrorState) {
      let { tr } = prosemirrorState;
      let mentionSpec = prosemirrorState.schema.nodes.mention;
      let typeOfMention = 'admin';
      if (selectedItem.entityType === EntityType.Team) {
        typeOfMention = 'team';
      } else if (selectedItem.entityType === EntityType.SlackUser) {
        typeOfMention = 'slack_user';
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: 'created',
        object: `${typeOfMention}_mention`,
        place: 'inbox',
      });
      let name = selectedItem.data.name;

      let uri = `//${this.regionService.appHost}/apps/${this.session.workspace.id}/${typeOfMention}/${selectedItem.data.id}`;
      if (selectedItem.entityType === EntityType.SlackUser) {
        uri = `slack://${selectedItem.data.type}/${selectedItem.data.id}`;
        name = `@${name}`;
      }

      let node = mentionSpec.createAndFill({
        name,
        uri,
      });

      tr.delete(this.args.matchResult.range.from, this.args.matchResult.range.to);
      if (node) {
        tr.insert(this.args.matchResult.range.from, node);
      }
      this.args.composer.commands.dispatch(tr);
      this.args.composer.commands.insertText(' ');
      this.args.composer.commands.focus();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Composer::MentionTypeahead': typeof MentionTypeahead;
    'inbox2/composer/mention-typeahead': typeof MentionTypeahead;
  }
}
